import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	Legend,
	LinearScale,
	Title,
	Tooltip,
	ChartData
} from "chart.js";

import { GraphicBarContainer, GraphicBox, LegendColor, LegendItem, LegendList } from './styles';
import { useIntl } from 'react-intl';
import ChartDataLabels from 'chartjs-plugin-datalabels';

type AxisConfig = {
	xLabel?: string;
	yLabel?: string;
	maxValue?: number;
};

type DatasetEntry = {
	id: string;
	label: string;
	value: number;
	color?: string;
};

type GroupedDatasetEntry = {
	id: string;
	label: string;
	values: DatasetEntry[];
};

type LegendEntry = {
	label: string;
	color: string;
};

export type GenericGraphicBarData = {
	title?: string;
	axis?: AxisConfig;
	dataset?: DatasetEntry[];
	groupedDataset?: GroupedDatasetEntry[];
};

type Props = {
	dataProps: GenericGraphicBarData;
	title?: string;
	fallbackColors?: string[];
	showLegend?: boolean;
	sizingPreset?: 'default' | 'compact';
	onEndReached?: () => void;
	hasMore?: boolean;
	isLoading?: boolean;
	valueUnit?: string;
	onBarClick?: (entry: DatasetEntry | GroupedDatasetEntry) => void;
	showPointerOnHover?: boolean;
};

const DEFAULT_COLORS = [
	'#123E88',
	'#2F6BC6',
	'#7BBCE3',
	'#9B8CEB',
	'#5E4DB2',
	'#606775',
	'#0E1A66',
	'#4F9B8A',
	'#379659',
	'#D9822B',
];

const MOBILE_BREAKPOINT = 768;
const MIN_BAR_WIDTH = 90;
const MIN_CANVAS_WIDTH = 480;
const MIN_CANVAS_HEIGHT = 320;

export const GenericGraphicBar = ({
	dataProps,
	fallbackColors = DEFAULT_COLORS,
	showLegend = false,
	sizingPreset = 'default',
	onEndReached,
	hasMore,
	isLoading,
	valueUnit = '',
	onBarClick,
	showPointerOnHover = false,
}: Props) => {
	ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
	const intl = useIntl();

	const payload = useMemo(
		() => dataProps ?? { dataset: [], groupedDataset: [], axis: undefined, title: undefined },
		[dataProps],
	);

	const dataset = useMemo(() => payload.dataset ?? [], [payload]);
	const groupedDataset = useMemo(() => payload.groupedDataset ?? [], [payload]);
	const hasGroupedData = groupedDataset.length > 0;
	const axis = useMemo(() => payload.axis ?? {}, [payload]);

	const [isMobile, setIsMobile] = useState<boolean>(
		() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT,
	);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const handleResize = () => {
			setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		if (!onEndReached || isLoading || !hasMore) return;

		const target = e.currentTarget;
		const threshold = 50;

		const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + threshold;

		const isRight = target.scrollWidth - target.scrollLeft <= target.clientWidth + threshold;

		if (isBottom || isRight) {
			onEndReached();
		}
	};

	const bars = useMemo(
		() =>
			hasGroupedData
				? []
				: dataset.map((entry, index) => ({
					...entry,
					displayColor: entry.color ?? fallbackColors[index % fallbackColors.length],
				})),
		[dataset, fallbackColors, hasGroupedData],
	);

	const groupedSeries = useMemo(() => {
		if (!hasGroupedData) return null;

		const equipmentMap = new Map<string, LegendEntry>();

		groupedDataset.forEach(group => {
			group.values.forEach(value => {
				if (equipmentMap.has(value.id)) return;

				const assignedColor =
					value.color ?? fallbackColors[equipmentMap.size % fallbackColors.length];

				equipmentMap.set(value.id, {
					label: value.label,
					color: assignedColor,
				});
			});
		});

		const equipmentEntries = Array.from(equipmentMap.entries());
		const labels = groupedDataset.map(group => group.label);

		const groupedSizing = sizingPreset === 'compact'
			? { bar: 0.75, category: 0.72 }
			: { bar: 0.9, category: 0.82 };

		const datasets: ChartData<'bar'>['datasets'] = equipmentEntries.map(([equipmentId, info]) => ({
			label: info.label,
			data: groupedDataset.map(group => {
				const match = group.values.find(value => value.id === equipmentId);
				return match?.value ?? 0;
			}),
			backgroundColor: info.color,
			barPercentage: groupedSizing.bar,
			categoryPercentage: groupedSizing.category,
			borderRadius: 0,
			borderSkipped: false,
		}));

		const legendEntries = equipmentEntries.map(([, info]) => info);

		return {
			labels,
			datasets,
			totalBars: groupedDataset.length * Math.max(equipmentEntries.length, 1),
			legendEntries,
		};
	}, [fallbackColors, groupedDataset, hasGroupedData, sizingPreset]);

	const allValues = useMemo(() => {
		if (hasGroupedData) {
			return groupedDataset.flatMap(group => group.values.map(item => item.value));
		}
		return dataset.map(item => item.value);
	}, [dataset, groupedDataset, hasGroupedData]);

	const suggestedMaxY = useMemo(() => {
		const highestValue = allValues.reduce((acc, value) => Math.max(acc, value), 0);

		if (highestValue === 0) return axis.maxValue ?? 10;

		const baseMax = axis.maxValue && axis.maxValue > highestValue ? axis.maxValue : highestValue;
		const padded = Math.max(baseMax, highestValue * 1.15);
		const stepPower = Math.max(Math.floor(Math.log10(padded)) - 1, 0);
		const step = Math.pow(10, stepPower) || 1;

		return Math.ceil(padded / step) * step;
	}, [allValues, axis.maxValue]);

	const chartData = useMemo<ChartData<'bar'>>(
		() => {
			const singleSizing = sizingPreset === 'compact'
				? { bar: isMobile ? 0.7 : 0.65, category: 0.85 }
				: { bar: isMobile ? 0.85 : 0.9, category: 1 };

			if (hasGroupedData && groupedSeries) {
				return {
					labels: groupedSeries.labels,
					datasets: groupedSeries.datasets,
				};
			}

			return {
				labels: bars.map(item => item.label),
				datasets: [
					{
						label: axis.yLabel ?? '',
						data: bars.map(item => item.value),
						backgroundColor: bars.map(item => item.displayColor),
						barPercentage: singleSizing.bar,
						categoryPercentage: singleSizing.category,
						borderRadius: 0,
						borderSkipped: false,
					},
				],
			};
		},
		[axis.yLabel, bars, groupedSeries, hasGroupedData, isMobile, sizingPreset],
	);

	const chartOptions = useMemo<ChartOptions<'bar'>>(
		() => ({
			indexAxis: isMobile ? 'y' : 'x',
			responsive: true,
			maintainAspectRatio: false,
			animation: { duration: 300 },
			plugins: {
				legend: { display: false },
				title: { display: false },
				datalabels: {
					display: true,
					anchor: 'end',
					align: isMobile ? 'right' : 'top',
					offset: isMobile ? 8 : 4,
					clip: false,
					clamp: true,
					formatter: value => `${intl.formatNumber(value as number)}${valueUnit}`,
					font: { weight: 'bold', size: 12 },
				},
			},
			layout: isMobile ? { padding: { right: 30, bottom: 32 } } : undefined,
			onClick: (_event, elements) => {
				if (!onBarClick || elements.length === 0) return;

				const { index } = elements[0];

				if (hasGroupedData) {
					const group = groupedDataset[index];
					if (group) onBarClick(group);
					return;
				}

				const entry = bars[index];
				if (entry) onBarClick(entry);
			},
			onHover: (event, elements) => {
				if (!showPointerOnHover) return;
				const target = event?.native?.target as HTMLCanvasElement | undefined;
				if (!target) return;
				target.style.cursor = onBarClick && elements.length ? 'pointer' : 'default';
			},
			scales: {
				x: {
					grid: {
						display: true,
					},
					ticks: {
						font: { size: 13, weight: 'bold' },
						maxRotation: 0,
						minRotation: 0,
					},
					title: {
						display: true,
						text: isMobile ? axis.yLabel ?? 'Média por hora' : axis.xLabel ?? 'Forno',
						font: { size: 13, weight: 'bold' },
						padding: { top: 20 },
					},
					...(isMobile ? { max: suggestedMaxY } : {}),
				},
				y: {
					grid: {
						display: true,
					},
					beginAtZero: true,
					suggestedMax: suggestedMaxY,
					...(isMobile ? {} : { max: suggestedMaxY }),
					ticks: {
						precision: 0,
						stepSize:
							suggestedMaxY > 0
								? Math.max(Math.round(suggestedMaxY / 5), 1)
								: undefined,
						font: { size: 12 },
					},
					title: {
						display: true,
						text: isMobile ? axis.xLabel ?? 'Forno' : axis.yLabel ?? 'Média por hora',
						font: { size: 13, weight: 'bold' },
						padding: { bottom: 10 },
					},
				},
			},
		}),
		[axis, bars, groupedDataset, hasGroupedData, isMobile, onBarClick, suggestedMaxY, showPointerOnHover, intl, valueUnit],
	);

	const totalBarsCount = hasGroupedData
		? groupedSeries?.totalBars ?? groupedDataset.length
		: dataset.length;

	const baseCategoryCount = hasGroupedData ? groupedDataset.length : dataset.length;
	const needsHorizontalScroll = !isMobile && baseCategoryCount > 8;
	const minHorizontalWidth = Math.max(baseCategoryCount * MIN_BAR_WIDTH, MIN_CANVAS_WIDTH);

	const chartWrapperStyle = useMemo(
		() =>
			isMobile
				? { minHeight: `${Math.max(totalBarsCount * 56, MIN_CANVAS_HEIGHT)}px` }
				: needsHorizontalScroll
					? { minWidth: `${minHorizontalWidth}px` }
					: { width: '100%', maxWidth: '100%' },
		[isMobile, minHorizontalWidth, needsHorizontalScroll, totalBarsCount],
	);

	const legendEntries = useMemo<LegendEntry[]>(() => {
		if (!showLegend) return [];
		if (hasGroupedData) {
			return groupedSeries?.legendEntries ?? [];
		}

		return bars.map(item => ({
			label: item.label,
			color: item.displayColor,
		}));
	}, [bars, groupedSeries, hasGroupedData, showLegend]);

	const chartKey = useMemo(() => {
		if (hasGroupedData) {
			return groupedDataset
				.map(group =>
					`${group.id}:${group.values.map(item => `${item.id}-${item.value}`).join(',')}`,
				)
				.join('|');
		}

		return bars.map(item => `${item.id}-${item.value}`).join('|');
	}, [bars, groupedDataset, hasGroupedData]);

	const chartRenderKey = useMemo(
		() => `${chartKey}-${isMobile ? 'm' : 'd'}`,
		[chartKey, isMobile],
	);

	return (
		<GraphicBarContainer>
			<GraphicBox
				onScroll={handleScroll}
				data-scroll={isMobile ? 'vertical' : needsHorizontalScroll ? 'horizontal' : 'none'}
				style={{
					overflowY: isMobile ? 'auto' : 'hidden',
					overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
					maxHeight: isMobile ? '400px' : 'unset',
				}}
			>
				<div
					className="graphic-content"
					style={chartWrapperStyle}
				>
					<Bar
						className="bar"
						options={chartOptions}
						data={chartData}
						key={chartRenderKey}
						plugins={[ChartDataLabels]}
					/>
				</div>
			</GraphicBox>
			{legendEntries.length > 0 && (
				<LegendList>
					{legendEntries.map(entry => (
						<LegendItem key={entry.label}>
							<LegendColor style={{ backgroundColor: entry.color }} />
							<span>{entry.label}</span>
						</LegendItem>
					))}
				</LegendList>
			)}
		</GraphicBarContainer>
	);
};
